import React, { useEffect, useState } from "react";
import { Alert, Button, Checkbox, Modal, Skeleton, Typography } from "antd";
import { CheckCircleFilled, CloseCircleFilled, ExclamationCircleFilled } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import {
  formatCents,
  formatDate
} from "../../../serviceProvider/components/accounting/shared/money";
import * as accounting from "../../../serviceProvider/components/accounting/receivables/api";
import { DOCUMENT_ONLY_TYPES } from "../../../utils/invoiceStatusArray";
import { invoiceErrorText, preflightText } from "./invoiceErrors";
import { Success } from "../../Messages/Messages";
import "./InvoiceDialogs.css";

const CREDIT_SOURCES = {
  credit_note: "From an invoice correction",
  cancellation: "From a cancellation",
  overpayment: "From an overpayment",
  manual: "Manual credit"
};

/**
 * PA-1 / CB-3: before finalizing, the Pflichtangaben checklist (errors block, warnings need a
 * confirmation) and the customer's open credits, which can be offset against this invoice.
 */
const SendDialog = ({ open, invoice, email, onClose, onSent }) => {
  const { t: ta } = useTranslation("accounting");
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [findings, setFindings] = useState([]);
  const [credits, setCredits] = useState([]);
  const [selected, setSelected] = useState([]);
  const [error, setError] = useState("");

  const receivable = invoice && !DOCUMENT_ONLY_TYPES.includes(invoice.type);
  const clientId = invoice?.client?._id || invoice?.client;

  useEffect(() => {
    if (!open || !invoice?._id) return;
    setError("");
    setSelected([]);
    setCredits([]);
    setLoading(true);
    const checks = accounting
      .preflight(invoice._id)
      .then(res => setFindings(Array.isArray(res.data?.findings) ? res.data.findings : []))
      .catch(err => {
        setFindings([]);
        setError(invoiceErrorText(ta, err));
      });
    const creditLoad =
      receivable && clientId
        ? accounting
            .listCredits({ customer: clientId, status: "open" })
            .then(res =>
              setCredits(
                (Array.isArray(res.data?.credits) ? res.data.credits : []).filter(
                  c => Number(c.remaining) > 0
                )
              )
            )
            .catch(() => setCredits([]))
        : Promise.resolve();
    Promise.all([checks, creditLoad]).finally(() => setLoading(false));
  }, [open, invoice?._id]);

  const errors = findings.filter(f => f.severity === "error");
  const warnings = findings.filter(f => f.severity !== "error");
  const open_ = Number(invoice?.amounts?.open ?? invoice?.amounts?.gross ?? 0);
  const offsetTotal = Math.min(
    open_,
    credits.filter(c => selected.includes(String(c._id))).reduce((s, c) => s + c.remaining, 0)
  );

  const send = async () => {
    setBusy(true);
    setError("");
    try {
      const res = await accounting.sendInvoice(invoice._id, { email, offsetCredits: selected });
      Success(`${ta("Invoice finalized")}: ${res.data.invoice.invoiceNumber}`);
      onSent?.(res.data.invoice);
    } catch (err) {
      const info = accounting.errorInfo(err);
      if (info.code === "MISSING_MANDATORY" && Array.isArray(info.details))
        setFindings(info.details);
      setError(invoiceErrorText(ta, err));
    } finally {
      setBusy(false);
    }
  };

  const icon = f =>
    f.severity === "error" ? (
      <CloseCircleFilled className="invoice-dialog__check--error" />
    ) : (
      <ExclamationCircleFilled className="invoice-dialog__check--warning" />
    );

  return (
    <Modal
      rootClassName="invoice-dialog"
      title={email ? ta("Finalize & send") : ta("Finalize")}
      open={open}
      width={640}
      onCancel={onClose}
      destroyOnClose
      footer={[
        <Button key="cancel" onClick={onClose}>
          {ta("Cancel")}
        </Button>,
        <Button
          key="send"
          type="primary"
          loading={busy}
          disabled={loading || errors.length > 0}
          onClick={send}
        >
          {warnings.length
            ? ta("Finalize despite warnings")
            : email
              ? ta("Finalize & send")
              : ta("Finalize")}
        </Button>
      ]}
    >
      {loading ? (
        <Skeleton active />
      ) : (
        <div className="invoice-dialog__body">
          <Typography.Paragraph type="secondary">
            {email
              ? ta("Finalize and email the invoice to the customer?")
              : ta("Allocate the invoice number, post it to the ledger and lock the invoice?")}
          </Typography.Paragraph>
          {error ? <Alert type="error" showIcon message={error} /> : null}
          <section>
            <Typography.Title level={5}>{ta("Required invoice details")}</Typography.Title>
            {findings.length ? (
              <ul className="invoice-dialog__checklist">
                {[...errors, ...warnings].map((f, i) => (
                  <li key={`${f.field}-${i}`}>
                    {icon(f)}
                    <span>{preflightText(ta, f)}</span>
                  </li>
                ))}
              </ul>
            ) : (
              <div className="invoice-dialog__checklist-ok">
                <CheckCircleFilled className="invoice-dialog__check--ok" />
                <span>{ta("All required details are present")}</span>
              </div>
            )}
            {errors.length ? (
              <Alert
                type="error"
                showIcon
                message={ta("Complete the missing details before the invoice can be sent.")}
              />
            ) : warnings.length ? (
              <Alert
                type="warning"
                showIcon
                message={ta("Check the warnings; the invoice can still be sent.")}
              />
            ) : null}
          </section>
          {credits.length ? (
            <section>
              <Typography.Title level={5}>{ta("Offset customer credit")}</Typography.Title>
              <Typography.Text type="secondary" className="invoice-dialog__hint">
                {ta("The customer has open credits. Selected credits reduce the amount due.")}
              </Typography.Text>
              <Checkbox.Group
                className="invoice-dialog__credits"
                value={selected}
                onChange={v => setSelected(v)}
              >
                {credits.map(c => (
                  <Checkbox key={String(c._id)} value={String(c._id)}>
                    <span className="invoice-dialog__credit">
                      <span>
                        {formatDate(c.date)} ·{" "}
                        {ta(CREDIT_SOURCES[c.source?.kind] || "Customer credit")}
                        {c.note ? ` · ${c.note}` : ""}
                      </span>
                      <strong>{formatCents(c.remaining)}</strong>
                    </span>
                  </Checkbox>
                ))}
              </Checkbox.Group>
              {selected.length ? (
                <Alert
                  type="info"
                  showIcon
                  message={ta("{{amount}} will be offset; {{rest}} remains due", {
                    amount: formatCents(offsetTotal),
                    rest: formatCents(Math.max(0, open_ - offsetTotal))
                  })}
                />
              ) : null}
            </section>
          ) : null}
        </div>
      )}
    </Modal>
  );
};

export default SendDialog;
